import React, { useState, useEffect, useRef } from 'react';
import { Card, Typography, Row, Col, Button, Space, Select, Table, Tag, Alert, Tabs, Timeline, Divider } from 'antd';
import { 
  ForkOutlined, 
  MergeOutlined, 
  ShareAltOutlined,
  NodeIndexOutlined,
  BranchesOutlined,
  ApiOutlined,
  ClusterOutlined,
  ApartmentOutlined
} from '@ant-design/icons';
import * as d3 from 'd3';
import { CHART_DIMENSIONS, SVG_STYLES } from '../config/chartStyles';

const { Title, Paragraph } = Typography;
const { Option } = Select;

interface ProcessNode {
  id: string;
  name: string;
  type: 'start' | 'process' | 'aggregate' | 'decision' | 'end';
  process: string; // 所属流程
  aggregates?: string[]; // 涉及的聚合根
  x?: number;
  y?: number;
}

interface ProcessLink {
  source: string;
  target: string;
  type: 'flow' | 'aggregate' | 'event';
  label?: string;
}

interface AggregateRoot {
  id: string;
  name: string;
  nameCn: string;
  description: string;
  processes: string[]; // 参与的流程
  events: string[]; // 发布的领域事件
}

const ProcessIntegration: React.FC = () => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [selectedView, setSelectedView] = useState<'flow' | 'aggregate' | 'event'>('flow');
  const [selectedProcess, setSelectedProcess] = useState<string[]>(['order', 'shipment', 'billing', 'quotation']);
  const [selectedAggregate, setSelectedAggregate] = useState<string | null>(null);

  // 聚合根定义
  const aggregateRoots: AggregateRoot[] = [
    {
      id: 'order',
      name: 'Order',
      nameCn: '订单',
      description: '管理订单生命周期，协调客户需求与运输服务',
      processes: ['order', 'quotation', 'shipment'],
      events: ['OrderCreated', 'OrderConfirmed', 'OrderCancelled']
    },
    {
      id: 'customer',
      name: 'Customer',
      nameCn: '客户',
      description: '管理客户信息和信用，跨多个流程共享',
      processes: ['order', 'quotation', 'billing'],
      events: ['CustomerCreated', 'CreditLimitChanged', 'CustomerBlocked']
    },
    {
      id: 'shipment',
      name: 'Shipment',
      nameCn: '运输单',
      description: '管理运输执行，连接订单与实际货运',
      processes: ['shipment', 'tracking', 'customs'],
      events: ['ShipmentCreated', 'ShipmentDeparted', 'ShipmentArrived']
    },
    {
      id: 'billing',
      name: 'Billing',
      nameCn: '账单',
      description: '管理费用计算和账单生成',
      processes: ['billing', 'settlement', 'quotation'],
      events: ['InvoiceGenerated', 'PaymentReceived', 'OverdueNotified']
    }
  ];

  // 流程节点定义 - Y坐标将在drawFlowView中动态计算
  const processNodes: ProcessNode[] = [
    // 订单流程
    { id: 'order-start', name: '客户下单', type: 'start', process: 'order', x: 120 },
    { id: 'order-check', name: '信用检查', type: 'process', process: 'order', aggregates: ['customer'], x: 280 },
    { id: 'order-create', name: '创建订单', type: 'aggregate', process: 'order', aggregates: ['order'], x: 440 },
    { id: 'order-quote', name: '生成报价', type: 'process', process: 'order', aggregates: ['order', 'billing'], x: 600 },
    
    // 运输流程
    { id: 'ship-start', name: '订单确认', type: 'start', process: 'shipment', x: 120 },
    { id: 'ship-create', name: '创建运输单', type: 'aggregate', process: 'shipment', aggregates: ['shipment', 'order'], x: 280 },
    { id: 'ship-arrange', name: '安排运输', type: 'process', process: 'shipment', aggregates: ['shipment'], x: 440 },
    { id: 'ship-depart', name: '货物启运', type: 'process', process: 'shipment', aggregates: ['shipment'], x: 600 },
    
    // 账单流程
    { id: 'bill-start', name: '运输完成', type: 'start', process: 'billing', x: 120 },
    { id: 'bill-calc', name: '费用计算', type: 'process', process: 'billing', aggregates: ['billing', 'shipment'], x: 280 },
    { id: 'bill-generate', name: '生成账单', type: 'aggregate', process: 'billing', aggregates: ['billing', 'customer'], x: 440 },
    { id: 'bill-send', name: '发送账单', type: 'process', process: 'billing', aggregates: ['billing'], x: 600 },
    
    // 报价流程
    { id: 'quote-start', name: '客户询价', type: 'start', process: 'quotation', x: 120 },
    { id: 'quote-analyze', name: '需求分析', type: 'process', process: 'quotation', aggregates: ['customer'], x: 280 },
    { id: 'quote-calculate', name: '费用计算', type: 'process', process: 'quotation', aggregates: ['billing'], x: 440 },
    { id: 'quote-generate', name: '生成报价', type: 'aggregate', process: 'quotation', aggregates: ['billing', 'customer'], x: 600 }
  ];

  // 流程连接
  const processLinks: ProcessLink[] = [
    // 订单流程内部
    { source: 'order-start', target: 'order-check', type: 'flow' },
    { source: 'order-check', target: 'order-create', type: 'flow' },
    { source: 'order-create', target: 'order-quote', type: 'flow' },
    
    // 运输流程内部
    { source: 'ship-start', target: 'ship-create', type: 'flow' },
    { source: 'ship-create', target: 'ship-arrange', type: 'flow' },
    { source: 'ship-arrange', target: 'ship-depart', type: 'flow' },
    
    // 账单流程内部
    { source: 'bill-start', target: 'bill-calc', type: 'flow' },
    { source: 'bill-calc', target: 'bill-generate', type: 'flow' },
    { source: 'bill-generate', target: 'bill-send', type: 'flow' },
    
    // 报价流程内部
    { source: 'quote-start', target: 'quote-analyze', type: 'flow' },
    { source: 'quote-analyze', target: 'quote-calculate', type: 'flow' },
    { source: 'quote-calculate', target: 'quote-generate', type: 'flow' },
    
    // 跨流程聚合根连接
    { source: 'order-create', target: 'ship-create', type: 'aggregate', label: 'Order聚合' },
    { source: 'ship-depart', target: 'bill-calc', type: 'aggregate', label: 'Shipment聚合' },
    { source: 'order-check', target: 'bill-generate', type: 'aggregate', label: 'Customer聚合' },
    { source: 'quote-generate', target: 'order-create', type: 'aggregate', label: 'Quote转Order' },
    { source: 'quote-calculate', target: 'bill-calc', type: 'aggregate', label: '共享费用计算' }
  ];

  // 领域事件流
  const domainEvents = [
    { event: 'OrderCreated', source: 'order', targets: ['shipment', 'billing'], description: '触发运输单创建和费用预算' },
    { event: 'ShipmentDeparted', source: 'shipment', targets: ['tracking', 'customer'], description: '通知跟踪系统和客户' },
    { event: 'InvoiceGenerated', source: 'billing', targets: ['customer', 'finance'], description: '通知客户和财务系统' },
    { event: 'CreditLimitChanged', source: 'customer', targets: ['order', 'billing'], description: '影响订单审批和账期' },
    { event: 'QuotationGenerated', source: 'quotation', targets: ['customer', 'order'], description: '报价完成，等待客户确认转订单' },
    { event: 'QuotationAccepted', source: 'quotation', targets: ['order'], description: '报价被接受，触发订单创建' }
  ];

  // 绘制流程图
  const drawDiagram = () => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 800;
    const height = 750;
    svg.attr('width', width).attr('height', height);

    // 创建图层
    const g = svg.append('g');

    // 添加背景
    g.append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('fill', '#f0f2f5');

    if (selectedView === 'flow') {
      // 流程视图 - 显示流程内部流转
      drawFlowView(g, width, height);
    } else if (selectedView === 'aggregate') {
      // 聚合根视图 - 显示聚合根之间的关系
      drawAggregateView(g, width, height);
    } else if (selectedView === 'event') {
      // 事件视图 - 显示领域事件流
      drawEventView(g, width, height);
    }
  };

  // 绘制流程视图
  const drawFlowView = (g: any, width: number, height: number) => {
    // 绘制流程泳道
    const processes = ['order', 'shipment', 'billing', 'quotation'];
    const laneHeight = height / processes.length;
    
    // 为节点计算正确的Y坐标（每个泳道的中心）
    const processYPositions: Record<string, number> = {};
    processes.forEach((process, i) => {
      processYPositions[process] = i * laneHeight + laneHeight / 2;
    });
    
    // 更新节点的Y坐标
    processNodes.forEach(node => {
      node.y = processYPositions[node.process];
    });

    processes.forEach((process, i) => {
      // 泳道背景
      g.append('rect')
        .attr('x', 0)
        .attr('y', i * laneHeight)
        .attr('width', width)
        .attr('height', laneHeight)
        .attr('fill', i % 2 === 0 ? '#fff' : '#fafafa')
        .attr('stroke', '#ddd');

      // 泳道标题背景
      g.append('rect')
        .attr('x', 0)
        .attr('y', i * laneHeight)
        .attr('width', 80)
        .attr('height', laneHeight)
        .attr('fill', '#f6f8fa')
        .attr('stroke', '#ddd');
        
      // 泳道标题
      g.append('text')
        .attr('x', 40)
        .attr('y', i * laneHeight + laneHeight / 2)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', '14px')
        .attr('font-weight', 'bold')
        .attr('fill', '#333')
        .text(process === 'order' ? '订单流程' : 
              process === 'shipment' ? '运输流程' : 
              process === 'billing' ? '账单流程' : '报价流程')
        .attr('transform', `rotate(-90 ${40} ${i * laneHeight + laneHeight / 2})`);
    });

    // 只显示流程内部的连接
    const flowLinks = processLinks.filter(link => link.type === 'flow');
    
    // 绘制连接线
    const linkGroup = g.append('g');
    
    flowLinks.forEach(link => {
      const source = processNodes.find(n => n.id === link.source);
      const target = processNodes.find(n => n.id === link.target);
      
      if (source && target && selectedProcess.includes(source.process)) {
        const path = linkGroup.append('path')
          .attr('d', `M${source.x},${source.y} L${target.x},${target.y}`)
          .attr('stroke', '#1890ff')
          .attr('stroke-width', 2)
          .attr('fill', 'none')
          .attr('opacity', 0.8);

        // 添加箭头
        const arrowId = `arrow-${link.source}-${link.target}`;
        g.append('defs').append('marker')
          .attr('id', arrowId)
          .attr('viewBox', '0 -5 10 10')
          .attr('refX', 25)
          .attr('refY', 0)
          .attr('markerWidth', 6)
          .attr('markerHeight', 6)
          .attr('orient', 'auto')
          .append('path')
          .attr('d', 'M0,-5L10,0L0,5')
          .attr('fill', '#1890ff');

        path.attr('marker-end', `url(#${arrowId})`);
      }
    });

    // 绘制节点
    const nodeGroup = g.append('g');
    
    processNodes.forEach(node => {
      // 只显示选中流程的节点
      if (!selectedProcess.includes(node.process)) return;

      const isHighlighted = selectedAggregate && node.aggregates && node.aggregates.includes(selectedAggregate);
      
      // 节点形状
      if (node.type === 'start') {
        // 开始节点 - 圆形
        nodeGroup.append('circle')
          .attr('cx', node.x!)
          .attr('cy', node.y!)
          .attr('r', 20)
          .attr('fill', '#52c41a')
          .attr('stroke', isHighlighted ? '#ff4d4f' : '#fff')
          .attr('stroke-width', isHighlighted ? 3 : 2)
          .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))');
      } else if (node.type === 'aggregate') {
        // 聚合根节点 - 六边形
        const hexagon = d3.path();
        const r = 25;
        for (let i = 0; i < 6; i++) {
          const angle = (Math.PI / 3) * i;
          const x = node.x! + r * Math.cos(angle);
          const y = node.y! + r * Math.sin(angle);
          if (i === 0) hexagon.moveTo(x, y);
          else hexagon.lineTo(x, y);
        }
        hexagon.closePath();
        
        nodeGroup.append('path')
          .attr('d', hexagon.toString())
          .attr('fill', '#1890ff')
          .attr('stroke', isHighlighted ? '#ff4d4f' : '#fff')
          .attr('stroke-width', isHighlighted ? 3 : 2)
          .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))');
      } else {
        // 普通节点 - 矩形
        nodeGroup.append('rect')
          .attr('x', node.x! - 45)
          .attr('y', node.y! - 18)
          .attr('width', 90)
          .attr('height', 36)
          .attr('rx', 18)
          .attr('fill', '#69c0ff')
          .attr('stroke', isHighlighted ? '#ff4d4f' : '#fff')
          .attr('stroke-width', isHighlighted ? 3 : 2)
          .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))');
      }

      // 节点文本
      nodeGroup.append('text')
        .attr('x', node.x!)
        .attr('y', node.y! + 4)
        .attr('text-anchor', 'middle')
        .attr('font-size', '11px')
        .attr('fill', 'white')
        .attr('font-weight', 'bold')
        .text(node.name);
    });
    
    // 添加泳道分隔线，使布局更清晰
    for (let i = 1; i < processes.length; i++) {
      g.append('line')
        .attr('x1', 0)
        .attr('y1', i * laneHeight)
        .attr('x2', width)
        .attr('y2', i * laneHeight)
        .attr('stroke', '#e8e8e8')
        .attr('stroke-width', 2);
    }
  };

  // 绘制聚合根视图
  const drawAggregateView = (g: any, width: number, height: number) => {
    // 添加标题
    g.append('text')
      .attr('x', width / 2)
      .attr('y', 25)
      .attr('text-anchor', 'middle')
      .attr('font-size', '18px')
      .attr('font-weight', 'bold')
      .attr('fill', '#333')
      .text('DDD聚合设计');
      
    // 添加说明文字
    g.append('text')
      .attr('x', width / 2)
      .attr('y', 45)
      .attr('text-anchor', 'middle')
      .attr('font-size', '12px')
      .attr('fill', '#666')
      .text('聚合是一组相关对象的集合，作为数据修改的单元。每个聚合都有一个聚合根，外部只能通过聚合根来访问聚合内的对象。');
      
    // 添加交互提示
    g.append('rect')
      .attr('x', width - 200)
      .attr('y', 10)
      .attr('width', 180)
      .attr('height', 30)
      .attr('rx', 15)
      .attr('fill', '#e6f7ff')
      .attr('stroke', '#91d5ff')
      .attr('stroke-width', 1);
      
    g.append('text')
      .attr('x', width - 110)
      .attr('y', 28)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#1890ff')
      .attr('font-weight', 'bold')
      .text('💡 点击聚合查看关系详情');
    
    // 定义聚合的详细信息
    const aggregateBoxes = [
      {
        id: 'order',
        name: '订单聚合',
        x: 50,
        y: 80,
        width: 350,
        height: 280,
        color: '#1890ff',
        aggregateRoot: {
          name: 'Order (订单)',
          methods: ['createOrder()', 'cancelOrder()', 'updateStatus()'],
          properties: ['orderId', 'customerId', 'totalAmount', 'status']
        },
        entities: [
          {
            name: 'OrderItem (订单项)',
            properties: ['productId', 'quantity', 'price'],
            position: { x: 20, y: 150 }
          },
          {
            name: 'OrderStatus (订单状态)',
            properties: ['status', 'timestamp', 'reason'],
            position: { x: 180, y: 150 }
          }
        ],
        valueObjects: [
          { name: 'Money (金额)', position: { x: 20, y: 230 } },
          { name: 'Address (地址)', position: { x: 180, y: 230 } }
        ]
      },
      {
        id: 'shipment',
        name: '运输聚合',
        x: 420,
        y: 80,
        width: 330,
        height: 280,
        color: '#fa8c16',
        aggregateRoot: {
          name: 'Shipment (运输单)',
          methods: ['createShipment()', 'updateRoute()', 'markDelivered()'],
          properties: ['shipmentNo', 'orderId', 'status', 'eta']
        },
        entities: [
          {
            name: 'Cargo (货物)',
            properties: ['description', 'weight', 'volume'],
            position: { x: 20, y: 150 }
          },
          {
            name: 'ShipmentEvent (运输事件)',
            properties: ['eventType', 'location', 'timestamp'],
            position: { x: 170, y: 150 }
          }
        ],
        valueObjects: [
          { name: 'Route (路线)', position: { x: 20, y: 230 } },
          { name: 'Dimensions (尺寸)', position: { x: 170, y: 230 } }
        ]
      },
      {
        id: 'customer',
        name: '客户聚合',
        x: 50,
        y: 380,
        width: 350,
        height: 250,
        color: '#52c41a',
        aggregateRoot: {
          name: 'Customer (客户)',
          methods: ['updateCreditLimit()', 'blockCustomer()', 'addContact()'],
          properties: ['customerId', 'name', 'creditLimit', 'status']
        },
        entities: [
          {
            name: 'CustomerCredit (信用记录)',
            properties: ['limit', 'used', 'available'],
            position: { x: 20, y: 150 }
          },
          {
            name: 'CustomerContact (联系人)',
            properties: ['name', 'phone', 'email', 'role'],
            position: { x: 180, y: 150 }
          }
        ],
        valueObjects: []
      },
      {
        id: 'billing',
        name: '账单聚合',
        x: 420,
        y: 380,
        width: 330,
        height: 250,
        color: '#722ed1',
        aggregateRoot: {
          name: 'Invoice (账单)',
          methods: ['generateInvoice()', 'recordPayment()', 'markOverdue()'],
          properties: ['invoiceNo', 'customerId', 'totalAmount', 'dueDate']
        },
        entities: [
          {
            name: 'ChargeItem (费用项)',
            properties: ['chargeCode', 'amount', 'description'],
            position: { x: 20, y: 150 }
          },
          {
            name: 'Payment (付款记录)',
            properties: ['amount', 'paymentDate', 'method'],
            position: { x: 170, y: 150 }
          }
        ],
        valueObjects: []
      }
    ];

    // 绘制聚合盒子
    aggregateBoxes.forEach(aggregate => {
      const isSelected = aggregate.id === selectedAggregate;
      
      // 聚合边界框
      g.append('rect')
        .attr('x', aggregate.x)
        .attr('y', aggregate.y)
        .attr('width', aggregate.width)
        .attr('height', aggregate.height)
        .attr('rx', 8)
        .attr('fill', isSelected ? aggregate.color : '#fff')
        .attr('fill-opacity', isSelected ? 0.1 : 1)
        .attr('stroke', aggregate.color)
        .attr('stroke-width', isSelected ? 3 : 2)
        .attr('stroke-dasharray', '8,4')
        .style('cursor', 'pointer')
        .on('click', () => {
          setSelectedAggregate(aggregate.id === selectedAggregate ? null : aggregate.id);
        });
      
      // 聚合标题
      g.append('rect')
        .attr('x', aggregate.x)
        .attr('y', aggregate.y)
        .attr('width', aggregate.width)
        .attr('height', 30)
        .attr('rx', '8 8 0 0')
        .attr('fill', aggregate.color)
        .attr('fill-opacity', 0.9);
        
      g.append('text')
        .attr('x', aggregate.x + aggregate.width / 2)
        .attr('y', aggregate.y + 20)
        .attr('text-anchor', 'middle')
        .attr('font-size', '14px')
        .attr('fill', 'white')
        .attr('font-weight', 'bold')
        .text(aggregate.name);
      
      // 聚合根
      const rootY = aggregate.y + 50;
      g.append('rect')
        .attr('x', aggregate.x + 10)
        .attr('y', rootY)
        .attr('width', aggregate.width - 20)
        .attr('height', 80)
        .attr('rx', 4)
        .attr('fill', aggregate.color)
        .attr('fill-opacity', 0.2)
        .attr('stroke', aggregate.color)
        .attr('stroke-width', 2);
      
      // 聚合根皇冠图标
      g.append('text')
        .attr('x', aggregate.x + 20)
        .attr('y', rootY + 20)
        .attr('font-size', '16px')
        .text('👑');
        
      g.append('text')
        .attr('x', aggregate.x + 45)
        .attr('y', rootY + 20)
        .attr('font-size', '13px')
        .attr('font-weight', 'bold')
        .attr('fill', aggregate.color)
        .text(aggregate.aggregateRoot.name);
      
      // 聚合根属性
      g.append('text')
        .attr('x', aggregate.x + 20)
        .attr('y', rootY + 40)
        .attr('font-size', '11px')
        .attr('fill', '#666')
        .text(`属性: ${aggregate.aggregateRoot.properties.join(', ')}`);
        
      // 聚合根方法
      g.append('text')
        .attr('x', aggregate.x + 20)
        .attr('y', rootY + 60)
        .attr('font-size', '11px')
        .attr('fill', '#666')
        .text(`方法: ${aggregate.aggregateRoot.methods.join(', ')}`);
      
      // 实体
      aggregate.entities.forEach(entity => {
        const entityX = aggregate.x + entity.position.x;
        const entityY = aggregate.y + entity.position.y;
        
        g.append('rect')
          .attr('x', entityX)
          .attr('y', entityY)
          .attr('width', 140)
          .attr('height', 60)
          .attr('rx', 4)
          .attr('fill', '#f0f2f5')
          .attr('stroke', '#d9d9d9')
          .attr('stroke-width', 1);
          
        g.append('text')
          .attr('x', entityX + 70)
          .attr('y', entityY + 20)
          .attr('text-anchor', 'middle')
          .attr('font-size', '12px')
          .attr('font-weight', 'bold')
          .attr('fill', '#333')
          .text(entity.name);
          
        g.append('text')
          .attr('x', entityX + 10)
          .attr('y', entityY + 40)
          .attr('font-size', '10px')
          .attr('fill', '#666')
          .text(entity.properties.join(', '))
          .call(wrapText, 120);
      });
      
      // 值对象
      aggregate.valueObjects.forEach(vo => {
        const voX = aggregate.x + vo.position.x;
        const voY = aggregate.y + vo.position.y;
        
        g.append('ellipse')
          .attr('cx', voX + 70)
          .attr('cy', voY + 20)
          .attr('rx', 70)
          .attr('ry', 20)
          .attr('fill', '#e6f7ff')
          .attr('stroke', '#91d5ff')
          .attr('stroke-width', 1);
          
        g.append('text')
          .attr('x', voX + 70)
          .attr('y', voY + 25)
          .attr('text-anchor', 'middle')
          .attr('font-size', '11px')
          .attr('fill', '#1890ff')
          .text(vo.name);
      });
    });
    
    // 简单的文本换行函数
    function wrapText(text: any, width: number) {
      text.each(function(this: any) {
        const text = d3.select(this);
        const words = text.text().split(/,\s*/);
        text.text('');
        
        let line = '';
        let lineNumber = 0;
        const x = text.attr('x');
        const y = text.attr('y');
        
        words.forEach((word: string, i: number) => {
          if (i > 0 && i % 2 === 0) {
            text.append('tspan')
              .attr('x', x)
              .attr('y', y)
              .attr('dy', `${lineNumber * 1.1}em`)
              .text(line);
            line = word + ', ';
            lineNumber++;
          } else {
            line += word + (i < words.length - 1 ? ', ' : '');
          }
        });
        
        if (line) {
          text.append('tspan')
            .attr('x', x)
            .attr('y', y)
            .attr('dy', `${lineNumber * 1.1}em`)
            .text(line);
        }
      });
    }
    
    // 定义聚合间的交互关系
    const interactions = [
      { from: 'order', to: 'customer', type: 'reference', label: '引用', description: '订单引用客户ID' },
      { from: 'order', to: 'shipment', type: 'trigger', label: '触发', description: '订单确认后创建运输单' },
      { from: 'shipment', to: 'billing', type: 'trigger', label: '触发', description: '运输完成后生成账单' },
      { from: 'customer', to: 'billing', type: 'constraint', label: '约束', description: '客户信用影响账期' },
      { from: 'customer', to: 'order', type: 'reference', label: '关联', description: '客户下单' },
      { from: 'billing', to: 'shipment', type: 'reference', label: '依赖', description: '基于运输信息计费' }
    ];
    
    // 定义箭头标记
    const defs = g.append('defs');
    
    ['trigger', 'reference', 'constraint'].forEach(type => {
      defs.append('marker')
        .attr('id', `arrow-${type}`)
        .attr('viewBox', '0 -5 10 10')
        .attr('refX', 10)  // 增加refX值，让箭头显示在线条末端
        .attr('refY', 0)
        .attr('markerWidth', 8)  // 增大箭头尺寸
        .attr('markerHeight', 8)
        .attr('orient', 'auto')
        .append('path')
        .attr('d', 'M0,-5L10,0L0,5')
        .attr('fill', type === 'trigger' ? '#52c41a' : 
                     type === 'reference' ? '#1890ff' : '#fa8c16')
        .attr('opacity', 0.8);  // 增加不透明度
    });
    
    // 绘制所有聚合之间的交互关系
    const relationGroup = g.append('g').attr('class', 'relations');
    
    interactions.forEach(interaction => {
      const fromBox = aggregateBoxes.find(box => box.id === interaction.from);
      const toBox = aggregateBoxes.find(box => box.id === interaction.to);
      
      if (fromBox && toBox) {
        // 判断是否需要高亮
        const isHighlighted = selectedAggregate && 
          (interaction.from === selectedAggregate || interaction.to === selectedAggregate);
        
        // 计算连接点（从边框出发，而不是中心）
        const fromCenterX = fromBox.x + fromBox.width / 2;
        const fromCenterY = fromBox.y + fromBox.height / 2;
        const toCenterX = toBox.x + toBox.width / 2;
        const toCenterY = toBox.y + toBox.height / 2;
        
        // 计算角度和边框交点
        const angle = Math.atan2(toCenterY - fromCenterY, toCenterX - fromCenterX);
        
        // 从边框出发的点
        let fromX, fromY, toX, toY;
        
        // 简化计算：从盒子边缘出发，并为箭头预留空间
        const arrowOffset = 5; // 为箭头预留的空间
        if (Math.abs(angle) < Math.PI / 4) { // 右侧
          fromX = fromBox.x + fromBox.width;
          fromY = fromCenterY;
          toX = toBox.x - arrowOffset;
          toY = toCenterY;
        } else if (Math.abs(angle) > 3 * Math.PI / 4) { // 左侧
          fromX = fromBox.x;
          fromY = fromCenterY;
          toX = toBox.x + toBox.width + arrowOffset;
          toY = toCenterY;
        } else if (angle > 0) { // 下方
          fromX = fromCenterX;
          fromY = fromBox.y + fromBox.height;
          toX = toCenterX;
          toY = toBox.y - arrowOffset;
        } else { // 上方
          fromX = fromCenterX;
          fromY = fromBox.y;
          toX = toCenterX;
          toY = toBox.y + toBox.height + arrowOffset;
        }
        
        // 绘制交互线
        const path = relationGroup.append('path')
          .attr('d', `M${fromX},${fromY} L${toX},${toY}`)
          .attr('stroke', interaction.type === 'trigger' ? '#52c41a' : 
                         interaction.type === 'reference' ? '#1890ff' : '#fa8c16')
          .attr('stroke-width', isHighlighted ? 3 : 2)
          .attr('opacity', isHighlighted ? 1 : 0.6)  // 提高默认不透明度
          .attr('stroke-dasharray', interaction.type === 'reference' ? '5,5' : '0')
          .attr('marker-end', `url(#arrow-${interaction.type})`)
          .attr('fill', 'none')  // 确保路径不被填充
          .style('transition', 'all 0.3s ease');
        
        // 交互标签
        const midX = (fromX + toX) / 2;
        const midY = (fromY + toY) / 2;
        
        const labelGroup = relationGroup.append('g')
          .attr('opacity', isHighlighted ? 1 : 0.9);  // 提高标签的默认不透明度
        
        labelGroup.append('rect')
          .attr('x', midX - 30)
          .attr('y', midY - 10)
          .attr('width', 60)
          .attr('height', 20)
          .attr('rx', 10)
          .attr('fill', 'white')
          .attr('stroke', interaction.type === 'trigger' ? '#52c41a' : 
                         interaction.type === 'reference' ? '#1890ff' : '#fa8c16')
          .attr('stroke-width', 1);
          
        labelGroup.append('text')
          .attr('x', midX)
          .attr('y', midY + 4)
          .attr('text-anchor', 'middle')
          .attr('font-size', '10px')
          .attr('fill', '#333')
          .attr('font-weight', isHighlighted ? 'bold' : 'normal')
          .text(interaction.label);
          
        // 鼠标悬停时显示描述
        labelGroup
          .style('cursor', 'pointer')
          .append('title')
          .text(interaction.description);
      }
    });
    
    // 添加图例
    const legendY = height - 50;
    const legendItems = [
      { label: '👑 聚合根', x: 50 },
      { label: '□ 实体', x: 150 },
      { label: '○ 值对象', x: 250 },
      { label: '→ 触发关系', x: 350, color: '#52c41a' },
      { label: '⇢ 引用关系', x: 450, color: '#1890ff' },
      { label: '⟶ 约束关系', x: 550, color: '#fa8c16' }
    ];
    
    g.append('rect')
      .attr('x', 30)
      .attr('y', legendY - 15)
      .attr('width', 640)
      .attr('height', 40)
      .attr('rx', 4)
      .attr('fill', '#f0f2f5')
      .attr('stroke', '#d9d9d9')
      .attr('stroke-width', 1);
    
    legendItems.forEach(item => {
      g.append('text')
        .attr('x', item.x)
        .attr('y', legendY + 5)
        .attr('font-size', '12px')
        .attr('fill', item.color || '#666')
        .text(item.label);
    });
  };

  // 绘制事件视图
  const drawEventView = (g: any, width: number, height: number) => {
    // 添加标题
    g.append('text')
      .attr('x', width / 2)
      .attr('y', 30)
      .attr('text-anchor', 'middle')
      .attr('font-size', '18px')
      .attr('font-weight', 'bold')
      .attr('fill', '#333')
      .text('领域事件流转图');
    
    // 简化的事件流展示
    const flowSteps = [
      { 
        name: '客户下单', 
        aggregate: '客户聚合', 
        event: 'OrderCreated',
        description: '客户创建订单',
        x: 150, 
        y: 120 
      },
      { 
        name: '订单确认', 
        aggregate: '订单聚合', 
        event: 'OrderConfirmed',
        description: '订单状态确认',
        x: 350, 
        y: 120 
      },
      { 
        name: '创建运输', 
        aggregate: '运输聚合', 
        event: 'ShipmentCreated',
        description: '生成运输单',
        x: 550, 
        y: 120 
      },
      { 
        name: '生成账单', 
        aggregate: '账单聚合', 
        event: 'InvoiceGenerated',
        description: '计费完成',
        x: 350, 
        y: 280 
      }
    ];
    
    // 绘制事件流步骤
    flowSteps.forEach((step, i) => {
      // 步骤背景框
      g.append('rect')
        .attr('x', step.x - 80)
        .attr('y', step.y - 40)
        .attr('width', 160)
        .attr('height', 80)
        .attr('rx', 10)
        .attr('fill', '#f0f2ff')
        .attr('stroke', '#1890ff')
        .attr('stroke-width', 2);
      
      // 聚合根标识
      g.append('rect')
        .attr('x', step.x - 60)
        .attr('y', step.y - 30)
        .attr('width', 120)
        .attr('height', 20)
        .attr('rx', 10)
        .attr('fill', '#1890ff')
        .attr('stroke', 'none');
        
      g.append('text')
        .attr('x', step.x)
        .attr('y', step.y - 16)
        .attr('text-anchor', 'middle')
        .attr('font-size', '11px')
        .attr('fill', 'white')
        .attr('font-weight', 'bold')
        .text(step.aggregate);
      
      // 业务动作
      g.append('text')
        .attr('x', step.x)
        .attr('y', step.y + 5)
        .attr('text-anchor', 'middle')
        .attr('font-size', '14px')
        .attr('fill', '#333')
        .attr('font-weight', 'bold')
        .text(step.name);
      
      // 领域事件
      g.append('ellipse')
        .attr('cx', step.x)
        .attr('cy', step.y + 25)
        .attr('rx', 50)
        .attr('ry', 12)
        .attr('fill', '#722ed1')
        .attr('stroke', 'none');
        
      g.append('text')
        .attr('x', step.x)
        .attr('y', step.y + 29)
        .attr('text-anchor', 'middle')
        .attr('font-size', '10px')
        .attr('fill', 'white')
        .text(step.event);
    });
    
    // 绘制事件流向箭头
    const connections = [
      { from: 0, to: 1, label: '触发' },
      { from: 1, to: 2, label: '触发' },
      { from: 2, to: 3, label: '触发' }
    ];
    
    connections.forEach(conn => {
      const from = flowSteps[conn.from];
      const to = flowSteps[conn.to];
      
      // 计算箭头路径
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // 起点和终点偏移
      const startX = from.x + (dx / distance) * 80;
      const startY = from.y + (dy / distance) * 40;
      const endX = to.x - (dx / distance) * 80;
      const endY = to.y - (dy / distance) * 40;
      
      // 绘制箭头线
      g.append('line')
        .attr('x1', startX)
        .attr('y1', startY)
        .attr('x2', endX)
        .attr('y2', endY)
        .attr('stroke', '#52c41a')
        .attr('stroke-width', 3)
        .attr('marker-end', 'url(#event-arrow)')
        .attr('opacity', 0.8);
      
      // 箭头标签
      const midX = (startX + endX) / 2;
      const midY = (startY + endY) / 2;
      
      g.append('rect')
        .attr('x', midX - 15)
        .attr('y', midY - 8)
        .attr('width', 30)
        .attr('height', 16)
        .attr('rx', 8)
        .attr('fill', '#52c41a')
        .attr('opacity', 0.9);
        
      g.append('text')
        .attr('x', midX)
        .attr('y', midY + 3)
        .attr('text-anchor', 'middle')
        .attr('font-size', '9px')
        .attr('fill', 'white')
        .attr('font-weight', 'bold')
        .text(conn.label);
    });
    
    // 添加图例说明
    const legendY = height - 100;
    
    // 聚合根图例
    g.append('rect')
      .attr('x', 50)
      .attr('y', legendY - 10)
      .attr('width', 60)
      .attr('height', 20)
      .attr('rx', 10)
      .attr('fill', '#1890ff');
    g.append('text')
      .attr('x', 80)
      .attr('y', legendY + 4)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', 'white')
      .text('聚合根');
    
    // 领域事件图例
    g.append('ellipse')
      .attr('cx', 200)
      .attr('cy', legendY)
      .attr('rx', 30)
      .attr('ry', 10)
      .attr('fill', '#722ed1');
    g.append('text')
      .attr('x', 200)
      .attr('y', legendY + 3)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', 'white')
      .text('领域事件');
    
    // 事件流图例
    g.append('line')
      .attr('x1', 280)
      .attr('y1', legendY)
      .attr('x2', 320)
      .attr('y2', legendY)
      .attr('stroke', '#52c41a')
      .attr('stroke-width', 3)
      .attr('marker-end', 'url(#event-arrow)');
    g.append('text')
      .attr('x', 340)
      .attr('y', legendY + 4)
      .attr('font-size', '11px')
      .attr('fill', '#333')
      .text('事件流向');
    
    // 定义箭头标记
    const defs = g.append('defs');
    defs.append('marker')
      .attr('id', 'event-arrow')
      .attr('viewBox', '0 -5 10 10')
      .attr('refX', 8)
      .attr('refY', 0)
      .attr('markerWidth', 6)
      .attr('markerHeight', 6)
      .attr('orient', 'auto')
      .append('path')
      .attr('d', 'M0,-5L10,0L0,5')
      .attr('fill', '#52c41a');
  };

  useEffect(() => {
    drawDiagram();
  }, [selectedView, selectedProcess, selectedAggregate]);

  return (
    <div>
      <Title level={4}>
        <BranchesOutlined /> 流程整合视图
      </Title>
      
      <Paragraph>
        展示多个业务流程如何通过聚合根进行交互和整合。聚合根作为业务一致性的边界，在不同流程间共享业务规则和状态。
      </Paragraph>

      {/* 控制面板 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <span>视图模式：</span>
              <Select value={selectedView} onChange={setSelectedView} style={{ width: '100%' }}>
                <Option value="flow">流程视图</Option>
                <Option value="aggregate">聚合根视图</Option>
                <Option value="event">事件视图</Option>
              </Select>
            </Space>
          </Col>
          <Col span={8}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <span>选择流程：</span>
              <Select 
                mode="multiple" 
                value={selectedProcess} 
                onChange={setSelectedProcess}
                style={{ width: '100%' }}
              >
                <Option value="order">订单流程</Option>
                <Option value="shipment">运输流程</Option>
                <Option value="billing">账单流程</Option>
                <Option value="quotation">报价流程</Option>
              </Select>
            </Space>
          </Col>
          <Col span={6}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <span>高亮聚合根：</span>
              <Select 
                value={selectedAggregate} 
                onChange={setSelectedAggregate}
                style={{ width: '100%' }}
                allowClear
                placeholder="选择聚合根"
              >
                {aggregateRoots.map(ar => (
                  <Option key={ar.id} value={ar.id}>
                    {ar.nameCn} ({ar.name})
                  </Option>
                ))}
              </Select>
            </Space>
          </Col>
          <Col span={4}>
            <Button type="primary" icon={<MergeOutlined />} block>
              分析交集
            </Button>
          </Col>
        </Row>
      </Card>

      {/* 主视图区域 */}
      <Row gutter={16}>
        <Col span={16}>
          <Card title="流程整合图" size="small">
            <svg ref={svgRef} style={{ width: '100%', backgroundColor: '#f0f2f5' }} />
          </Card>
        </Col>
        
        <Col span={8}>
          <Card title="聚合根详情" size="small" style={{ marginBottom: 16 }}>
            {selectedAggregate ? (
              <div>
                {(() => {
                  const aggregate = aggregateRoots.find(ar => ar.id === selectedAggregate);
                  const aggDetail = [
                    {
                      id: 'order',
                      entities: ['Order', 'OrderItem', 'OrderStatus'],
                      boundary: '订单的创建、修改、取消必须通过Order聚合根',
                      reason: '保证订单项与订单状态的一致性',
                      rules: ['订单项不能独立存在', '状态变更需验证业务规则', '订单总额需实时更新']
                    },
                    {
                      id: 'customer',
                      entities: ['Customer', 'CustomerCredit', 'CustomerContact'],
                      boundary: '客户信息的所有变更通过Customer聚合根',
                      reason: '确保客户信用额度与联系信息的完整性',
                      rules: ['信用额度变更需审批', '联系人必须至少一个', '客户状态影响订单权限']
                    },
                    {
                      id: 'shipment',
                      entities: ['Shipment', 'Cargo', 'Route', 'ShipmentStatus'],
                      boundary: '运输单的所有操作必须通过Shipment聚合根',
                      reason: '保证货物、路线与运输状态的一致性',
                      rules: ['货物清单不能在运输中修改', '路线变更需重新计算时间', '状态必须按流程推进']
                    },
                    {
                      id: 'billing',
                      entities: ['Invoice', 'ChargeItem', 'Payment'],
                      boundary: '账单的生成和支付通过Billing聚合根',
                      reason: '确保费用项与支付记录的准确性',
                      rules: ['费用项必须有依据', '支付金额不能超过账单', '账单状态与支付同步']
                    }
                  ].find(d => d.id === selectedAggregate);
                  
                  return aggregate && aggDetail ? (
                    <>
                      <Title level={5}>{aggregate.nameCn} ({aggregate.name})</Title>
                      <Paragraph>{aggregate.description}</Paragraph>
                      
                      <Divider orientation="left" plain style={{ fontSize: '12px', margin: '12px 0' }}>
                        聚合边界
                      </Divider>
                      <Alert message={aggDetail.boundary} type="info" showIcon style={{ marginBottom: 8 }} />
                      
                      <Divider orientation="left" plain style={{ fontSize: '12px', margin: '12px 0' }}>
                        包含实体
                      </Divider>
                      <Space wrap>
                        {aggDetail.entities.map((entity, i) => (
                          <Tag key={entity} color={i === 0 ? 'blue' : 'default'}>
                            {i === 0 ? '🔹 ' : ''}{entity}
                          </Tag>
                        ))}
                      </Space>
                      
                      <Divider orientation="left" plain style={{ fontSize: '12px', margin: '12px 0' }}>
                        一致性规则
                      </Divider>
                      <ul style={{ fontSize: '12px', paddingLeft: 20, margin: 0 }}>
                        {aggDetail.rules.map(rule => (
                          <li key={rule}>{rule}</li>
                        ))}
                      </ul>
                      
                      <Divider orientation="left" plain style={{ fontSize: '12px', margin: '12px 0' }}>
                        领域事件
                      </Divider>
                      <Space wrap>
                        {aggregate.events.map(e => (
                          <Tag key={e} color="purple">{e}</Tag>
                        ))}
                      </Space>
                    </>
                  ) : null;
                })()}
              </div>
            ) : (
              <Alert message="请选择一个聚合根查看详情" type="info" />
            )}
          </Card>

          <Card title="交集分析" size="small">
            <Timeline>
              <Timeline.Item color="blue">
                订单流程创建Order聚合根
              </Timeline.Item>
              <Timeline.Item color="green">
                Order聚合根触发OrderCreated事件
              </Timeline.Item>
              <Timeline.Item color="red">
                运输流程监听事件，创建Shipment聚合根
              </Timeline.Item>
              <Timeline.Item color="orange">
                Shipment与Order通过ID关联
              </Timeline.Item>
            </Timeline>
          </Card>
        </Col>
      </Row>

      {/* 聚合根列表 */}
      <Card title="聚合根交集矩阵" style={{ marginTop: 16 }} size="small">
        <Table
          dataSource={aggregateRoots}
          columns={[
            {
              title: '聚合根',
              dataIndex: 'nameCn',
              key: 'name',
              render: (text, record) => (
                <Space>
                  <ClusterOutlined />
                  <strong>{text}</strong>
                  <Tag>{record.name}</Tag>
                </Space>
              )
            },
            {
              title: '参与流程',
              dataIndex: 'processes',
              key: 'processes',
              render: (processes: string[]) => (
                <Space wrap>
                  {processes.map(p => (
                    <Tag key={p} color="blue">{p}</Tag>
                  ))}
                </Space>
              )
            },
            {
              title: '交集点',
              key: 'intersection',
              render: (_, record) => {
                const intersections = aggregateRoots
                  .filter(ar => ar.id !== record.id)
                  .filter(ar => ar.processes.some(p => record.processes.includes(p)))
                  .map(ar => ar.nameCn);
                
                return intersections.length > 0 ? (
                  <Space wrap>
                    {intersections.map(name => (
                      <Tag key={name} color="orange">{name}</Tag>
                    ))}
                  </Space>
                ) : (
                  <span style={{ color: '#999' }}>-</span>
                );
              }
            },
            {
              title: '事件数',
              key: 'eventCount',
              render: (_, record) => (
                <Tag color="green">{record.events.length}</Tag>
              )
            }
          ]}
          pagination={false}
          size="small"
        />
      </Card>

      {/* 领域事件流 */}
      <Card title="领域事件流" style={{ marginTop: 16 }} size="small">
        <Alert
          message="领域事件是聚合根之间解耦通信的重要机制"
          description="当一个聚合根的状态发生变化时，它会发布领域事件。其他聚合根或流程可以订阅这些事件，实现业务流程的编排。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        
        <Table
          dataSource={domainEvents}
          columns={[
            {
              title: '事件名称',
              dataIndex: 'event',
              key: 'event',
              render: (text) => <Tag color="purple">{text}</Tag>
            },
            {
              title: '发布方',
              dataIndex: 'source',
              key: 'source',
              render: (text) => <Tag color="blue">{text}</Tag>
            },
            {
              title: '订阅方',
              dataIndex: 'targets',
              key: 'targets',
              render: (targets: string[]) => (
                <Space>
                  {targets.map(t => (
                    <Tag key={t} color="green">{t}</Tag>
                  ))}
                </Space>
              )
            },
            {
              title: '作用',
              dataIndex: 'description',
              key: 'description'
            }
          ]}
          pagination={false}
          size="small"
        />
      </Card>
    </div>
  );
};

export default ProcessIntegration;